import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import {
  COUNTRIES, COUNTRIES_DATA, JOB_CATEGORIES, ADMINISTRATION_TYPES,
} from '../constants/registration';

const EMPTY = {
  name: '',
  last_name: '',
  username: '',
  birth_date: '',
  position: '',
  department: '',
  job_category: '',
  administration_type: '',
  country: 'España',
  region: '',
  avatar: '' as string | null,
};

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState(EMPTY);
  const [original, setOriginal] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .schema('id')
        .from('users')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      const loaded = {
        name: data.name || '',
        last_name: data.last_name || '',
        username: data.username || '',
        birth_date: data.birth_date || '',
        position: data.position || '',
        department: data.institution || '',
        job_category: data.job_category || '',
        administration_type: data.administration_type || '',
        country: data.country || 'España',
        region: data.region || '',
        avatar: data.avatar || '',
      };
      setFormData(loaded);
      setOriginal(loaded);
    } catch (err: any) {
      console.error('Error loading profile:', err);
      setError('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof typeof EMPTY, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(original);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar los 5MB');
      return;
    }
    setUploadingAvatar(true);
    setError('');
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { cacheControl: '3600', upsert: false });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from('avatars').getPublicUrl(fileName);
      updateField('avatar', data.publicUrl);
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError('Error al subir la imagen: ' + (err.message || ''));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const { error } = await supabase
        .schema('id')
        .from('users')
        .update({
          name: formData.name,
          last_name: formData.last_name,
          username: formData.username,
          avatar: formData.avatar || null,
          birth_date: formData.birth_date || null,
          position: formData.position || null,
          institution: formData.department || null,
          job_category: formData.job_category || null,
          administration_type: formData.administration_type || null,
          country: formData.country || null,
          region: formData.region || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;
      setOriginal(formData);
      setMessage('Perfil actualizado correctamente');
    } catch (err: any) {
      console.error('Error saving profile:', err);
      setError('Error al guardar: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    setError('');
    setMessage('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: 'https://id.novagob.org/reset-password',
      });
      if (error) throw error;
      setMessage(`Te hemos enviado un correo a ${user.email} con el enlace para cambiar tu contraseña.`);
    } catch (err: any) {
      setError('Error al enviar el correo: ' + (err.message || ''));
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  const regions = COUNTRIES_DATA[formData.country] || [];

  return (
    <div className="container" style={{ marginTop: '40px', marginBottom: '80px' }}>
      <div className="card">
        <div className="form-header">
          <h1>Editar Perfil</h1>
          <p>Actualiza tus datos personales y profesionales</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <form onSubmit={handleSave} className="nomination-form">
          {/* Avatar */}
          <div className="form-group">
            <label className="form-label">Foto de Perfil</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {formData.avatar ? (
                <img
                  src={formData.avatar}
                  alt="Profile"
                  style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div
                  style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.05)',
                    border: '2px dashed rgba(255,255,255,0.2)',
                    color: 'rgba(255,255,255,0.4)',
                  }}
                >
                  <Camera size={24} />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarUpload}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? 'Subiendo...' : formData.avatar ? 'Cambiar foto' : 'Subir Foto'}
              </button>
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Nombre *</label>
              <input
                type="text" className="form-input" required
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Apellido *</label>
              <input
                type="text" className="form-input" required
                value={formData.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Username *</label>
              <input
                type="text" className="form-input" required
                value={formData.username}
                onChange={(e) => updateField('username', e.target.value.toLowerCase())}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de nacimiento</label>
              <input
                type="date" className="form-input"
                value={formData.birth_date}
                onChange={(e) => updateField('birth_date', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Categoría de puesto</label>
              <select
                className="form-select"
                value={formData.job_category}
                onChange={(e) => updateField('job_category', e.target.value)}
              >
                <option value="">Selecciona...</option>
                {JOB_CATEGORIES.map((j) => (
                  <option key={j} value={j}>{j}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de Administración</label>
              <select
                className="form-select"
                value={formData.administration_type}
                onChange={(e) => updateField('administration_type', e.target.value)}
              >
                <option value="">Selecciona...</option>
                {ADMINISTRATION_TYPES.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Posición / Cargo</label>
              <input
                type="text" className="form-input"
                value={formData.position}
                onChange={(e) => updateField('position', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Institución / Organización / Empresa</label>
              <input
                type="text" className="form-input"
                value={formData.department}
                onChange={(e) => updateField('department', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">País</label>
              <select
                className="form-select"
                value={formData.country}
                onChange={(e) => {
                  updateField('country', e.target.value);
                  updateField('region', '');
                }}
              >
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Región</label>
              <select
                className="form-select"
                value={formData.region}
                onChange={(e) => updateField('region', e.target.value)}
                disabled={regions.length === 0}
              >
                <option value="">Selecciona...</option>
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--color-border)', margin: '24px 0' }} />

          <div className="form-group">
            <label className="form-label">Seguridad</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSendPasswordReset}
                disabled={sendingReset}
              >
                {sendingReset ? 'Enviando...' : 'Cambiar contraseña'}
              </button>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Te enviaremos un correo con un enlace para establecer una nueva contraseña.
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ flex: 1 }}
              onClick={() => navigate('/dashboard')}
            >
              Volver
            </button>
            {hasChanges && (
              <button
                type="submit"
                className="btn btn-primary"
                style={{ flex: 1 }}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
